import React from "react";
import type { ConfigData } from "../../services/UserConfigurationService";
import {
  ConfigurationSettingType,
  SchemaPropertyDescriptor,
  UserConfigurationSchemaDescriptor,
  normalizeConfigurationSettingType,
} from "../../services/userConfigurationSchemaService";
import {
  CASH_BASED_DEPLOYMENT_KEY,
  getCashDeploymentDisplay,
  type CashDeploymentDisplayTone,
} from "../../utils/cashDeploymentSettings";

type ConfigurationValueState = "true" | "false" | "unset" | "value";

interface ConfigurationSetting {
  name: string;
  type: ConfigurationSettingType;
}

interface UserConfigurationSettingsSummaryProps {
  appearance?: "default" | "alpha";
  descriptor: UserConfigurationSchemaDescriptor | null;
  data: ConfigData | null | undefined;
  isDarkMode: boolean;
  isLoading: boolean;
}

const ROOT_CONFIGURATION_KEYS = [
  "isbeta",
  "iscustomer",
  "isretirement",
  "tradingenabled",
];
const USER_SETTING_KEYS = [
  "user_tradingenabled",
  CASH_BASED_DEPLOYMENT_KEY,
];

const formatConfigurationLabel = (name: string): string =>
  name
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const getUserSettingLabel = (name: string): string => {
  if (name === "user_tradingenabled") return "User Trading Enabled";
  if (name === CASH_BASED_DEPLOYMENT_KEY) return "New Cash Deployment";
  return formatConfigurationLabel(name);
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readUserSettingRawValue = (
  data: ConfigData | null | undefined,
  settingName: string
): unknown => {
  if (!isObjectRecord(data?.userSettings)) return undefined;
  return data.userSettings[settingName];
};

const cashDeploymentBadgeClass = (
  tone: CashDeploymentDisplayTone,
  appearance: "default" | "alpha",
): string => {
  if (appearance === "alpha") return tone === "auto-deploy" ? "bg-v7-success/10 text-v7-success" : "bg-v7-paper text-v7-bronze-dark";
  if (tone === "reserve") {
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
  }
  if (tone === "auto-deploy") {
    return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
  }
  return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
};

const toSetting = (
  property: SchemaPropertyDescriptor
): ConfigurationSetting => ({
  name: property.name,
  type: normalizeConfigurationSettingType(property.type),
});

const uniqueSettings = (
  settings: ConfigurationSetting[]
): ConfigurationSetting[] => {
  const seen = new Set<string>();
  return settings.filter((setting) => {
    if (seen.has(setting.name)) return false;
    seen.add(setting.name);
    return true;
  });
};

const getRootSettings = (
  descriptor: UserConfigurationSchemaDescriptor | null
): ConfigurationSetting[] =>
  uniqueSettings([
    ...ROOT_CONFIGURATION_KEYS.map((name) => ({
      name,
      type: "boolean" as const,
    })),
    ...(descriptor?.rootLevelProperties ?? []).map(toSetting),
  ]);

const getUserSettings = (
  descriptor: UserConfigurationSchemaDescriptor | null
): ConfigurationSetting[] =>
  uniqueSettings([
    ...USER_SETTING_KEYS.map((name) => ({
      name,
      type: "boolean" as const,
    })),
    ...(descriptor?.userSettingsProperties ?? []).map(toSetting),
  ]);

const readConfigurationValueDisplay = (
  data: ConfigData | null | undefined,
  setting: ConfigurationSetting,
  scope: "root" | "userSettings"
): { state: ConfigurationValueState; label: string } => {
  const rawValue =
    scope === "root"
      ? data?.[setting.name]
      : isObjectRecord(data?.userSettings)
        ? data.userSettings[setting.name]
        : undefined;

  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return { state: "unset", label: "unset" };
  }
  if (setting.type !== "boolean") {
    return { state: "value", label: String(rawValue) };
  }
  if (typeof rawValue === "boolean") {
    return {
      state: rawValue ? "true" : "false",
      label: rawValue ? "true" : "false",
    };
  }
  const normalized = String(rawValue).trim().toLowerCase();
  if (normalized === "true") return { state: "true", label: "true" };
  if (normalized === "false") return { state: "false", label: "false" };
  return { state: "unset", label: "unset" };
};

const configurationValueBadgeClass = (
  state: ConfigurationValueState,
  appearance: "default" | "alpha",
): string => {
  if (appearance === "alpha") return state === "true" ? "bg-v7-success/10 text-v7-success" : state === "unset" ? "bg-v7-paper text-v7-bronze-dark" : "bg-v7-mist text-v7-ink-soft";
  if (state === "true") {
    return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
  }
  if (state === "false" || state === "value") {
    return "bg-gray-100 text-gray-700 dark:bg-rp-surface-raised dark:text-gray-300";
  }
  return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400";
};

const SettingsGroup: React.FC<{
  appearance?: "default" | "alpha";
  title: string;
  settings: ConfigurationSetting[];
  data: ConfigData | null | undefined;
  scope: "root" | "userSettings";
  isDarkMode: boolean;
  isLoading: boolean;
}> = ({ appearance = "default", title, settings, data, scope, isDarkMode, isLoading }) => (
  <div
    className={appearance === "alpha" ? `rounded-v7-control border p-2 border-v7-ink/15 bg-v7-paper` : `rounded-md border p-2 ${
      isDarkMode
        ? "border-gray-700 bg-gray-800"
        : "border-gray-100 bg-gray-50"
    }`}
  >
    <div
      className={appearance === "alpha" ? `mb-2 text-xs font-semibold uppercase tracking-wider text-v7-ink/60` : `mb-2 text-xs font-semibold uppercase tracking-wider ${
        isDarkMode ? "text-gray-500" : "text-gray-400"
      }`}
    >
      {title}
    </div>
    {isLoading ? (
      <div className={appearance === "alpha" ? "h-5 animate-pulse rounded-v7-control bg-v7-mist" : "h-5 animate-pulse rounded bg-gray-200 dark:bg-gray-800"} />
    ) : (
      <div className="space-y-1.5">
        {settings.map((setting) => {
          const { state, label } = readConfigurationValueDisplay(
            data,
            setting,
            scope
          );
          const cashDeploymentDisplay =
            setting.name === CASH_BASED_DEPLOYMENT_KEY &&
            scope === "userSettings"
              ? getCashDeploymentDisplay(
                  readUserSettingRawValue(data, setting.name)
                )
              : null;
          const badgeLabel = cashDeploymentDisplay?.label ?? label;
          const badgeClass = cashDeploymentDisplay
            ? cashDeploymentBadgeClass(cashDeploymentDisplay.tone, appearance)
            : configurationValueBadgeClass(state, appearance);

          return (
            <div
              key={`${scope}:${setting.name}`}
              className="flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <div
                  className={appearance === "alpha" ? `truncate font-mono text-xs text-v7-ink-soft` : `truncate font-mono text-xs ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {setting.name}
                </div>
                <div
                  className={appearance === "alpha" ? `truncate text-[10px] text-v7-ink/60` : `truncate text-[10px] ${
                    isDarkMode ? "text-gray-500" : "text-gray-400"
                  }`}
                >
                  {scope === "userSettings"
                    ? getUserSettingLabel(setting.name)
                    : formatConfigurationLabel(setting.name)}
                  <span className="ml-1 uppercase">{setting.type}</span>
                </div>
              </div>
              <span
                className={appearance === "alpha" ? `shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wider ${cashDeploymentDisplay ? "normal-case" : "uppercase"} ${badgeClass}` : `shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wider ${
                  cashDeploymentDisplay ? "normal-case" : "uppercase"
                } ${badgeClass}`}
              >
                {badgeLabel}
              </span>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const UserConfigurationSettingsSummary: React.FC<
  UserConfigurationSettingsSummaryProps
> = ({
  appearance = "default", descriptor, data, isDarkMode, isLoading }) => (
  <div className="space-y-2">
    <SettingsGroup appearance={appearance}
      title="Admin Settings"
      settings={getRootSettings(descriptor)}
      data={data}
      scope="root"
      isDarkMode={isDarkMode}
      isLoading={isLoading}
    />
    <SettingsGroup appearance={appearance}
      title="User Settings"
      settings={getUserSettings(descriptor)}
      data={data}
      scope="userSettings"
      isDarkMode={isDarkMode}
      isLoading={isLoading}
    />
  </div>
);

export default UserConfigurationSettingsSummary;
